import { Checkbox } from '@components/ui/checkbox';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@components/ui/popover';
import { cn } from '@lib/utils';
import { ArrowRightIcon, ChevronRightIcon, FilterIcon } from 'lucide-react';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IconButton } from '../../core/icon-button.component';
import type { Column, ColumnDataType, DataTableFilterActions, FilterStrategy, FiltersState } from '../core/types';
import { isAnyOf } from '../lib/array';
import { getColumn } from '../lib/helpers';
import { type Locale, t } from '../lib/i18n';
import { FilterValueController } from './filter-value';

// Helper to render icon safely
function renderIcon(Icon: React.ComponentType<React.SVGProps<SVGSVGElement>> | undefined, className?: string) {
  if (!Icon) return null;
  return <Icon className={className} />;
}

type FilterSelectorProps<TData> = {
  active?: boolean;
  additionalContent?: React.ReactNode;
  filters: FiltersState;
  columns: Column<TData>[];
  actions: DataTableFilterActions;
  strategy: FilterStrategy;
  locale?: Locale;
};

export const FilterSelector = memo(__FilterSelector) as typeof __FilterSelector;

function __FilterSelector<TData>(props: FilterSelectorProps<TData>) {
  const { actions, active = false, additionalContent, columns, filters, locale = 'en', strategy } = props;
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const [property, setProperty] = useState<string | undefined>(undefined);
  const inputRef = useRef<HTMLInputElement>(null);

  const column = property ? getColumn(columns, property) : undefined;
  const filter = property ? filters.find((f) => f.columnId === property) : undefined;

  const hasFilters = active || filters.length > 0;

  useEffect(() => {
    if (property && inputRef) {
      inputRef.current?.focus();
      setValue('');
    }
  }, [property]);

  useEffect(() => {
    if (!open) setTimeout(() => setValue(''), 150);
  }, [open]);

  const content = useMemo(
    () =>
      property && column ? (
        <FilterValueController
          filter={filter!}
          column={column as Column<TData, ColumnDataType>}
          actions={actions}
          strategy={strategy}
          locale={locale}
        />
      ) : (
        <div className="flex flex-col">
          <Command
            loop
            filter={(value, search, keywords) => {
              const extendValue = `${value} ${keywords?.join(' ')}`;
              return extendValue.toLowerCase().includes(search.toLowerCase()) ? 1 : 0;
            }}
          >
            <CommandInput
              value={value}
              onValueChange={setValue}
              ref={inputRef}
              placeholder={t('search', locale)}
            />
            <CommandEmpty>{t('noresults', locale)}</CommandEmpty>
            <CommandList className="max-h-fit">
              <CommandGroup>
                {columns.map((column) => (
                  <FilterableColumn
                    key={column.id}
                    column={column}
                    setProperty={setProperty}
                  />
                ))}
                <QuickSearchFilters
                  search={value}
                  filters={filters}
                  columns={columns}
                  actions={actions}
                  strategy={strategy}
                  locale={locale}
                />
              </CommandGroup>
            </CommandList>
          </Command>
          {additionalContent}
        </div>
      ),
    [actions, additionalContent, column, columns, filter, filters, locale, property, strategy, value],
  );

  return (
    <Popover
      open={open}
      onOpenChange={async (value) => {
        setOpen(value);
        if (!value) setTimeout(() => setProperty(undefined), 100);
      }}
    >
      <PopoverTrigger asChild>
        <IconButton
          size="lg"
          icon={<FilterIcon className="size-4" />}
          variant={hasFilters ? 'active' : 'secondary'}
          className={cn('h-7')}
        />
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="bottom"
        className="w-fit p-0 origin-(--radix-popover-content-transform-origin)"
      >
        {content}
      </PopoverContent>
    </Popover>
  );
}

export function FilterableColumn<TData, TType extends ColumnDataType, TVal>({
  column,
  setProperty,
}: {
  column: Column<TData, TType, TVal>;
  setProperty: (value: string) => void;
}) {
  const itemRef = useRef<HTMLDivElement>(null);

  const prefetch = useCallback(() => {
    column.prefetchOptions();
    column.prefetchValues();
    column.prefetchFacetedUniqueValues();
    column.prefetchFacetedMinMaxValues();
  }, [column]);

  useEffect(() => {
    const target = itemRef.current;

    if (!target) return;

    // Set up MutationObserver
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'attributes') {
          const isSelected = target.getAttribute('data-selected') === 'true';
          if (isSelected) prefetch();
        }
      }
    });

    // Set up observer
    observer.observe(target, {
      attributes: true,
      attributeFilter: ['data-selected'],
    });

    // Cleanup on unmount
    return () => observer.disconnect();
  }, [prefetch]);

  return (
    <CommandItem
      ref={itemRef}
      value={column.id}
      keywords={[column.displayName]}
      onSelect={() => setProperty(column.id)}
      className="group"
      onMouseEnter={prefetch}
    >
      <div className="flex w-full items-center justify-between">
        <div className="inline-flex items-center gap-1.5">
          {
            <column.icon
              strokeWidth={2.25}
              className="size-4"
            />
          }
          <span>{column.displayName}</span>
        </div>
        <ArrowRightIcon className="size-4 opacity-0 group-aria-selected:opacity-100" />
      </div>
    </CommandItem>
  );
}

interface QuickSearchFiltersProps<TData> {
  search?: string;
  filters: FiltersState;
  columns: Column<TData>[];
  actions: DataTableFilterActions;
  strategy: FilterStrategy;
  locale?: Locale;
}

export const QuickSearchFilters = memo(__QuickSearchFilters) as typeof __QuickSearchFilters;

function __QuickSearchFilters<TData>({
  search,
  filters,
  columns,
  actions,
  strategy,
  locale = 'en',
}: QuickSearchFiltersProps<TData>) {
  const cols = useMemo(
    () => columns.filter((c) => isAnyOf<ColumnDataType>(c.type, ['option', 'multiOption'])),
    [columns],
  );

  if (!search || search.trim().length < 2) return null;

  return (
    <>
      {cols.map((column) => {
        const filter = filters.find((f) => f.columnId === column.id);
        const options = column.getOptions();
        const optionsCount = column.getFacetedUniqueValues();

        function handleOptionSelect(value: string, check: boolean) {
          if (check) actions.addFilterValue(column, [value]);
          else actions.removeFilterValue(column, [value]);
        }

        return (
          <React.Fragment key={column.id}>
            {options.map((v) => {
              const checked = Boolean(filter?.values.includes(v.value));
              const count = optionsCount?.get(v.value) ?? 0;

              return (
                <CommandItem
                  key={v.value}
                  value={v.value}
                  keywords={[v.label, v.value]}
                  onSelect={() => {
                    handleOptionSelect(v.value, !checked);
                  }}
                  className="group"
                >
                  <div className="flex items-center gap-1.5 group">
                    <Checkbox
                      checked={checked}
                      className="opacity-0 data-[state=checked]:opacity-100 group-data-[selected=true]:opacity-100 dark:border-ring mr-1"
                    />
                    <div className="flex items-center w-4 justify-center">
                      {renderIcon(v.icon, 'size-4 text-primary')}
                    </div>
                    <div className="flex items-center gap-0.5">
                      <span className="text-muted-foreground">{column.displayName}</span>
                      <ChevronRightIcon className="size-3.5 text-muted-foreground/75" />
                      <span>
                        {v.label}
                        <sup
                          className={cn(
                            !optionsCount && 'hidden',
                            'ml-0.5 tabular-nums tracking-tight text-muted-foreground',
                            count === 0 && 'slashed-zero',
                          )}
                        >
                          {count < 100 ? count : '100+'}
                        </sup>
                      </span>
                    </div>
                  </div>
                </CommandItem>
              );
            })}
          </React.Fragment>
        );
      })}
    </>
  );
}
